import { useCallback, useState } from 'react';
import { AlertCircle, History as HistoryIcon, RotateCcw, X } from 'lucide-react';
import type { SubmissionRow } from '../../types/submission';
import type { ChangeLogEntry } from '../../types/changelog';
import { useChangeLog } from '../../hooks/useChangeLog';
import { RestoreDialog, type RestoreTarget } from './RestoreDialog';

interface ResourceHistoryDrawerProps {
  resource: string;
  row: SubmissionRow;
  onClose: () => void;
  /** Called after a successful restore so the parent can refetch the list. */
  onRestored: () => void;
}

/**
 * Side drawer showing the changelog for a single submission. Entries
 * are rendered newest-first as the server returns them; the synthetic
 * `original` entry (when present) always renders at the bottom with a
 * "restore to original" affordance.
 *
 * Each real entry has its own "Restore to this version" button. Both
 * button flavours open the same `RestoreDialog` — it just carries the
 * target metadata.
 */
export function ResourceHistoryDrawer({
  resource,
  row,
  onClose,
  onRestored,
}: ResourceHistoryDrawerProps) {
  const { response, loading, error, refetch } = useChangeLog(resource, row.id);
  const [restoreTarget, setRestoreTarget] = useState<RestoreTarget | null>(null);

  const entries = response?.data ?? [];

  const handleRestored = useCallback(() => {
    setRestoreTarget(null);
    onRestored();
    refetch();
  }, [onRestored, refetch]);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-rgf-gray-200 px-5 py-4">
          <div className="flex items-start gap-2">
            <HistoryIcon className="mt-0.5 h-5 w-5 text-rgf-gray-500" />
            <div>
              <h2 className="text-base font-semibold text-rgf-gray-900">Change history</h2>
              <p className="mt-0.5 text-xs text-rgf-gray-500">
                {resource} · id {String(row.id)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <LoadingSkeleton />}
          {error && (
            <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-700" />
              <div className="text-red-900">{error.message}</div>
            </div>
          )}
          {!loading && !error && entries.length === 0 && (
            <div className="rounded-xl border border-dashed border-rgf-gray-200 p-6 text-center text-sm text-rgf-gray-500">
              No edits have been made to this record yet.
            </div>
          )}
          <ol className="space-y-3">
            {entries.map((entry) => (
              <li key={String(entry.id)}>
                <ChangeLogCard
                  entry={entry}
                  onRestore={() =>
                    setRestoreTarget({
                      change_log_id: entry.id,
                      to_original: entry.id === 'original' || entry.operation === 'ORIGINAL',
                      label: describeEntry(entry),
                    })
                  }
                />
              </li>
            ))}
          </ol>
        </div>
      </aside>

      {restoreTarget && (
        <RestoreDialog
          resource={resource}
          row={row}
          target={restoreTarget}
          onClose={() => setRestoreTarget(null)}
          onRestored={handleRestored}
        />
      )}
    </>
  );
}

function ChangeLogCard({
  entry,
  onRestore,
}: {
  entry: ChangeLogEntry;
  onRestore: () => void;
}) {
  const isOriginal = entry.id === 'original' || entry.operation === 'ORIGINAL';
  return (
    <div
      className={
        'rounded-xl border p-3 text-sm ' +
        (isOriginal ? 'border-rgf-primary-200 bg-rgf-primary-50' : 'border-rgf-gray-200 bg-white')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-rgf-gray-700 shadow-sm">
          {entry.operation.toLowerCase()}
        </span>
        <time className="text-xs text-rgf-gray-500">
          {formatTimestamp(entry.created_at)}
        </time>
      </div>
      <p className="mt-2 text-sm text-rgf-gray-900">{entry.change_reason}</p>
      <p className="mt-1 text-xs text-rgf-gray-500">by {entry.changed_by_email}</p>
      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onRestore}
          className="inline-flex items-center gap-1 rounded-md border border-rgf-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-rgf-gray-700 hover:border-rgf-gray-300 hover:text-rgf-gray-900"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {isOriginal ? 'Restore to original' : 'Restore to this version'}
        </button>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-xl border border-rgf-gray-200 p-3">
          <div className="h-4 w-1/3 animate-pulse rounded bg-rgf-gray-200" />
          <div className="mt-3 h-3 w-2/3 animate-pulse rounded bg-rgf-gray-100" />
          <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-rgf-gray-100" />
        </div>
      ))}
    </div>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function describeEntry(entry: ChangeLogEntry): string {
  if (entry.id === 'original' || entry.operation === 'ORIGINAL') return 'the original values';
  return `the ${entry.operation.toLowerCase()} from ${formatTimestamp(entry.created_at)}`;
}
